// IDs de medição são públicos. Não enviar dados do formulário aos provedores.
const GA4_ID = 'G-NFL6TK1M7V';
const META_PIXEL_ID = '1295618714204175';
const OPENAI_PIXEL_ID = 'GPL2BtW8maN9iaeAPUf61D';

type TagFunction = (...args: unknown[]) => void;
type MetaFunction = TagFunction & {
  callMethod?: TagFunction;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: TagFunction;
};
type TrackingWindow = Window & {
  dataLayer?: unknown[];
  gtag?: TagFunction;
  fbq?: MetaFunction;
  _fbq?: MetaFunction;
  oaiq?: TagFunction;
};

let inicializado = false;
const enviadosNestaPagina = new Set<string>();

const adicionarScript = (src: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

/** Carrega as tags somente depois de uma autorização específica de medição. */
export const iniciarMedicaoCadastro = () => {
  if (typeof window === 'undefined' || inicializado) return;
  inicializado = true;
  const trackingWindow = window as TrackingWindow;

  if (!trackingWindow.gtag) {
    const dataLayer = (trackingWindow.dataLayer ??= []);
    // O gtag.js só trata objetos `arguments` como comando; um array no dataLayer
    // é ignorado e nada chega ao GA4. Não trocar por rest params.
    trackingWindow.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      dataLayer.push(arguments);
    };
    trackingWindow.gtag('js', new Date());
    adicionarScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  }
  trackingWindow.gtag?.('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  trackingWindow.gtag?.('config', GA4_ID, { send_page_view: false });

  if (!trackingWindow.fbq) {
    const fbq: MetaFunction = (...args: unknown[]) => {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        (fbq.queue ??= []).push(args);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    trackingWindow.fbq = fbq;
    trackingWindow._fbq = fbq;
    adicionarScript('https://connect.facebook.net/en_US/fbevents.js');
  }
  // O pixel sobe ainda na tela com e-mail, telefone e CNPJ. Sem isto ele coleta
  // cliques e metadados da página, além do que o texto da autorização cobre.
  trackingWindow.fbq?.('set', 'autoConfig', false, META_PIXEL_ID);
  trackingWindow.fbq?.('init', META_PIXEL_ID);

  if (!trackingWindow.oaiq) {
    const queue: unknown[][] = [];
    trackingWindow.oaiq = (...args: unknown[]) => {
      queue.push(args);
    };
    (trackingWindow.oaiq as TagFunction & { q?: unknown[][] }).q = queue;
    adicionarScript('https://bzrcdn.openai.com/sdk/oaiq.min.js');
  }
  trackingWindow.oaiq?.('consent', false);
  trackingWindow.oaiq?.('init', { pixelId: OPENAI_PIXEL_ID });
  trackingWindow.oaiq?.('consent', true);
};

export const atualizarConsentimentoMedicao = (autorizado: boolean) => {
  if (typeof window === 'undefined') return;
  if (autorizado) iniciarMedicaoCadastro();
  if (!inicializado) return;
  const trackingWindow = window as TrackingWindow;
  const estado = autorizado ? 'granted' : 'denied';
  trackingWindow.gtag?.('consent', 'update', {
    analytics_storage: estado,
    ad_storage: estado,
    ad_user_data: estado,
    ad_personalization: estado,
  });
  trackingWindow.fbq?.(autorizado ? 'grantConsent' : 'revokeConsent');
  trackingWindow.oaiq?.('consent', autorizado);
};

/** Chamado somente após o backend confirmar que a conta foi criada/ativada. */
export const registrarCadastroConvertido = (leadId: string, autorizado: boolean) => {
  if (!autorizado || !leadId || typeof window === 'undefined') return;
  const dedupeKey = `notificacao-self:registration:${leadId}`;
  if (enviadosNestaPagina.has(dedupeKey)) return;
  try {
    if (window.sessionStorage.getItem(dedupeKey)) return;
  } catch {
    // Alguns navegadores bloqueiam storage. A proteção em memória permanece ativa.
  }

  iniciarMedicaoCadastro();
  const trackingWindow = window as TrackingWindow;
  const eventId = `notificacao-self-registration-${leadId}`;
  enviadosNestaPagina.add(dedupeKey);
  try {
    window.sessionStorage.setItem(dedupeKey, '1');
  } catch {
    // O cadastro não depende de storage de marketing.
  }

  trackingWindow.gtag?.('event', 'sign_up', { method: 'notificacao_self', send_to: GA4_ID });
  trackingWindow.fbq?.(
    'trackSingle',
    META_PIXEL_ID,
    'CompleteRegistration',
    {},
    { eventID: eventId },
  );
  trackingWindow.oaiq?.(
    'measure',
    'registration_completed',
    { type: 'customer_action' },
    { event_id: eventId },
  );
};
